/**
 * The top bar: global transaction search on the left, the Ask button on the
 * right. Nothing else — the bar is a tool, not a dashboard.
 *
 * Search submits to the transactions screen rather than opening a palette. In
 * a finance app "find that charge" is the question 90% of the time, and the
 * transactions screen is already the place that answers it properly.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { SearchIcon, SparklesIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAiPanel } from './ai-panel'
import { ScopeSwitch } from './ScopeSwitch'

export function CommandBar() {
  const navigate = useNavigate()
  const panel = useAiPanel()
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // ⌘K / Ctrl-K focuses search — the shortcut everyone already has in muscle memory.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    const query = text.trim()
    navigate(query ? `/transactions?text=${encodeURIComponent(query)}` : '/transactions')
  }

  return (
    // Opaque, not translucent. A blurred bar with the page smearing through it
    // is the one piece of glassmorphism this app had, and it cost contrast on
    // the only always-visible control — see the note in the summary.
    <header className="border-hairline bg-ink sticky top-0 z-20 flex h-14 items-center gap-2 border-b px-3 sm:px-4">
      <span className="font-display text-cyan mr-1 text-sm font-bold md:hidden" aria-hidden>
        PF
      </span>

      <form onSubmit={onSubmit} role="search" className="min-w-0 flex-1">
        <label htmlFor="global-search" className="sr-only">
          Search transactions
        </label>
        <div className="relative">
          <SearchIcon
            className="text-dim pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <input
            id="global-search"
            ref={inputRef}
            type="search"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Search transactions"
            className="border-hairline bg-panel placeholder:text-dim focus-visible:outline-ring h-9 w-full rounded-md border pr-12 pl-8 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 sm:max-w-md"
          />
          <kbd className="text-dim border-hairline pointer-events-none absolute top-1/2 right-2 hidden -translate-y-1/2 rounded border px-1 font-mono text-[0.625rem] sm:block">
            ⌘K
          </kbd>
        </div>
      </form>

      {/* Violet, like every AI surface in the app — you always know when you are
          about to read a model's output. */}
      <ScopeSwitch />

        <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={panel.toggle}
        aria-expanded={panel.open}
        aria-controls="ai-panel"
        className="border-violet/40 text-violet hover:bg-violet/10 hover:text-violet shrink-0"
      >
        <SparklesIcon aria-hidden />
        {/* Visually hidden below `sm` rather than removed: an icon-only button
            with no accessible name is an unnamed button to a screen reader. */}
        <span className="max-sm:sr-only">Ask</span>
      </Button>
    </header>
  )
}
